import './ButtonV2.scss';

import classNames from 'classnames';
import {
  ButtonHTMLAttributes,
  ForwardedRef,
  forwardRef,
  PropsWithChildren,
  ReactNode,
} from 'react';

import { Responsive } from '../../interfaces/Responsive';
import { splitDataAttributes } from '../../utils/dataAttributes';
import { ButtonSlot } from './ButtonSlot';

function getFluidClassname(fluid: ButtonV2Props['fluid']) {
  if (typeof fluid === 'boolean') {
    return fluid ? 'ds-Button--fluid' : '';
  }

  const sm = fluid?.sm ? `ds-Button--sm-fluid` : '';
  const md = fluid?.md ? `ds-Button--md-fluid` : '';
  const lg = fluid?.lg ? `ds-Button--lg-fluid` : '';
  return [sm, md, lg].join(' ');
}

type Sizes = 'small' | 'medium' | 'large' | 'huge';
function getSizeClassname(size: ButtonV2Props['size']) {
  if (typeof size === 'string') {
    return `ds-Button--${size}`;
  }

  const sm = `ds-Button--sm-${size?.sm ?? 'medium'}`;
  const md = `ds-Button--md-${size?.md ?? 'medium'}`;
  const lg = `ds-Button--lg-${size?.lg ?? 'medium'}`;
  return [sm, md, lg].join(' ');
}

function getFlavourClassname(
  intent: ButtonV2Props['intent'],
  variant: ButtonV2Props['variant'],
) {
  return `ds-Button--${intent}-${variant}`;
}

export type ButtonV2Props = ButtonHTMLAttributes<HTMLButtonElement> &
  PropsWithChildren<{
    asChild?: boolean;
    fluid?: boolean | Responsive<boolean>;
    icons?: {
      leading?: ReactNode;
      trailing?: ReactNode;
    };
    intent?:
      | 'neutral-gray'
      | 'brand-green'
      | 'destructive-red'
      | 'success-green'
      | 'warning-orange'
      | 'support-violet';
    size?: Sizes | Responsive<Sizes>;
    variant?: 'primary' | 'secondary' | 'tertiary' | 'tertiary-gray' | 'link';
  }>;

const Button = (props: ButtonV2Props, ref: ForwardedRef<HTMLButtonElement>) => {
  const {
    asChild,
    className: classNameProp,
    children,
    disabled = false,
    fluid = false,
    icons = undefined,
    intent = 'brand-green',
    size = 'medium',
    variant = 'secondary',
    ...reactButtonProps
  } = props;
  const { dataAttributes, ...restOfProps } =
    splitDataAttributes(reactButtonProps);
  const className = classNames(
    'ds-Button',
    getSizeClassname(size),
    getFlavourClassname(intent, variant),
    getFluidClassname(fluid),
    {
      disabled,
    },
    classNameProp,
  );

  if (asChild) {
    return (
      <ButtonSlot
        className={className}
        icons={icons}
        ref={ref}
        dataAttributes={dataAttributes}
        disabled={disabled}
      >
        {children}
      </ButtonSlot>
    );
  }

  return (
    <button
      type="button"
      disabled={disabled}
      className={className}
      ref={ref}
      {...dataAttributes}
      {...restOfProps}
    >
      {icons?.leading ?? null}
      {children}
      {icons?.trailing ?? null}
    </button>
  );
};

export const ButtonV2 = forwardRef<HTMLButtonElement, ButtonV2Props>(Button);
ButtonV2.displayName = 'Button';
